import { useEffect, useState, useContext } from "react";
import api from '../../helper/util/api';
import { AuthContext } from "../../helper/util/AuthContext";
import InputError from '../../helper/input/InputError';

export default function Profile() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPass, setSavingPass] = useState(false);

  const { user, updateUser } = useContext(AuthContext);

  const [profile, setProfile] = useState({
    name: "",
    email: "",
    mobile: "",
  });
  const [password, setPassword] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  // Load logged-in user profile
  useEffect(() => {
    setProfile({
      name: user.name,
      email: user.email,
      mobile: user.phone,
    });
  }, [user]);

  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPassword({ ...password, [e.target.name]: e.target.value });
  };

  //Validate name, email, and 10-digit phone number.
  function validateProfileDetails(data) {
    const errors = {};

    // Normalize and trim
    const name = (data.name ?? "").trim();
    const email = (data.email ?? "").trim();
    const mobile = (data.mobile ?? "").trim();

    // 1) Required checks
    if (!name) errors.name = "Name is required.";
    if (!email) errors.email = "Email is required.";
    if (!mobile) errors.mobile = "Mobile number is required.";

    // Only proceed if all required fields are present
    if (Object.keys(errors).length === 0) {
      // 2) Name sanity (letters, spaces, dots, hyphens, apostrophes; min 2 chars)
      // Adjust as needed for your use case
      if (name.length < 2) {
        errors.name = "Name must be at least 2 characters.";
      } else if (!/^[a-zA-Z.'\-\s]+$/.test(name)) {
        errors.name = "Name contains invalid characters.";
      }

      // 3) Email format (simple, practical regex)
      const emailRegex =
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (!emailRegex.test(email)) {
        errors.email = "Please enter a valid email address.";
      }

      // 4) Phone: exactly 10 digits (no spaces or symbols)
      // If you want to allow spaces/dashes and strip them, normalize before testing.
      if (!/^\d{10}$/.test(mobile)) {
        errors.mobile = "Phone number must be exactly 10 digits.";
      }
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }

  // Validate password change fields.
  function validatePasswordChange(data) {
    const errors = {};

    // Normalize inputs
    const currentPassword = (data.currentPassword ?? "").trim();
    const newPassword = (data.newPassword ?? "").trim();
    const confirmNewPassword = (data.confirmNewPassword ?? "").trim();

    // 1) Required fields
    if (!currentPassword) errors.currentPassword = "Current password is required.";
    if (!newPassword) errors.newPassword = "New password is required.";
    if (!confirmNewPassword) errors.confirmNewPassword = "Please confirm your new password.";

    // Only proceed if required fields present
    if (Object.keys(errors).length === 0) {
      // 2) Current must not equal new
      if (currentPassword === newPassword) {
        errors.newPassword = "New password must be different from the current password.";
      }

      // 3) New and confirm must match
      if (newPassword !== confirmNewPassword) {
        errors.confirmNewPassword = "New password and confirmation do not match.";
      }

      // 4) Strong password check (apply only if above two pass for newPassword)
      if (!errors.newPassword) {
        const strength = checkPasswordStrength(newPassword);
        if (!strength.ok) {
          errors.newPassword = strength.message;
        }
      }
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }

  // Enforce strong password rules.
  function checkPasswordStrength(pwd) {
    // Configurable policy
    const MIN_LENGTH = 8;            // Consider 12 for higher security
    const REQUIRE_LOWER = true;
    const REQUIRE_UPPER = true;
    const REQUIRE_DIGIT = true;
    const REQUIRE_SPECIAL = true;
    const DISALLOW_SPACES = true;

    // Quick checks
    if (pwd.length < MIN_LENGTH) {
      return { ok: false, message: `New password must be at least ${MIN_LENGTH} characters long.` };
    }
    if (DISALLOW_SPACES && /\s/.test(pwd)) {
      return { ok: false, message: "New password must not contain spaces." };
    }

    // Character class checks
    if (REQUIRE_LOWER && !/[a-z]/.test(pwd)) {
      return { ok: false, message: "New password must include at least one lowercase letter." };
    }
    if (REQUIRE_UPPER && !/[A-Z]/.test(pwd)) {
      return { ok: false, message: "New password must include at least one uppercase letter." };
    }
    if (REQUIRE_DIGIT && !/[0-9]/.test(pwd)) {
      return { ok: false, message: "New password must include at least one digit." };
    }
    if (REQUIRE_SPECIAL && !/[!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/`~]/.test(pwd)) {
      return { ok: false, message: "New password must include at least one special character." };
    }

    const normalized = pwd.toLowerCase().replace(/[^a-z0-9]/g, "");

    return { ok: true };
  }


  const updateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    const result = validateProfileDetails(profile);
    if(result.valid === false) {
      setValidationErrors(result.errors);
      setSaving(false);
      return;
    }

    try {
      await api.put("/profile/update-profile", {...profile, id: user.id});
      setMessage("Profile updated successfully");
      updateUser({ name: profile.name, email: profile.email, phone: profile.mobile });
    } catch (err) {
      setError(err.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    setSavingPass(true);
    setError("");
    setMessage("");

    const result = validatePasswordChange(password);
    if(result.valid === false) {
      setValidationErrors(result.errors);
      setSavingPass(false);
      return;
    }

    try {
      await api.put("/profile/update-password", {...password, id: user.id});
      setMessage("Password updated successfully");
      setPassword({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
    } catch (err) {
      setError(err.response?.data?.message || "Password update failed");
    } finally {
      setSavingPass(false);
    }
  };

  if (loading) return <div className="page-card">Loading profile...</div>;

  return (
    <div className="app-container">
      <div className="header">
        <h2>Profile</h2>
      </div>
      <div className="sub-header">
        <p>Manage your account information</p>
      </div>

      {(message || error) && (
        <div className={`alert ${error ? "alert-error" : "alert-success"}`}>
          {error || message}
        </div>
      )}

      <div className="panel">
        {/* Profile Info */}
        <div className="section-title"><h3>Edit Profile</h3></div>

        <form onSubmit={updateProfile}>
          <div className="form-row three-col-row" >
            <div>
              <label className="label">Name</label>
              <input name="name" value={profile.name} onChange={handleProfileChange} />
              <InputError errMsg={validationErrors.name} />
            </div>
            <div>
              <label className="label">Email</label>
              <input name="email" value={profile.email} onChange={handleProfileChange} />
              <InputError errMsg={validationErrors.email} />
            </div>
            <div>
              <label className="label">Mobile</label>
              <input name="mobile" value={profile.mobile} onChange={handleProfileChange} />
              <InputError errMsg={validationErrors.mobile} />
            </div>
          </div>
          <button disabled={saving}>
            {saving ? "Saving..." : "Update Profile"}
          </button>
        </form>
      </div>

        {/* Password */}
      <div className="panel" style={{marginTop: '10px'}}>
        <h3>Change Password</h3>

        <form onSubmit={updatePassword}>
          <div className="form-row three-col-row" >
            <div>
              <label className="label">Current Password</label>
              <input
                type="password"
                name="currentPassword"
                value={password.currentPassword}
                onChange={handlePasswordChange}
              />
              <InputError errMsg={validationErrors.currentPassword} />
            </div>

            <div>
              <label className="label">New Password</label>
              <input
                type="password"
                name="newPassword"
                value={password.newPassword}
                onChange={handlePasswordChange}
              />
              <InputError errMsg={validationErrors.newPassword} />
            </div>

            <div>
              <label className="label">Confirm New Password</label>
              <input
                type="password"
                name="confirmNewPassword"
                value={password.confirmNewPassword}
                onChange={handlePasswordChange}
              />
              <InputError errMsg={validationErrors.confirmNewPassword} />
            </div>
          </div>


          <button disabled={savingPass}>
            {savingPass ? "Updating..." : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}